import ai.rules.BucketFlow
import ai.rules.BucketOverrideEditor
import ai.rules.BudgetBucket
import ai.rules.BudgetClassificationEngine
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.encodeToString
import org.w3c.dom.HTMLElement
import org.w3c.fetch.RequestInit
import kotlin.js.json

/**
 * Lets the user re-bucket categories from the Needs & Wants Flow widget.
 *
 * This is the only surface on the web that can re-bucket a category, so it has
 * to carry everything the old Budget-page chips did: a mixed primary expands to
 * per-sub toggles, and any override can be handed back to the engine default.
 *
 * Everything that isn't markup — the row model, the shadowing rules and the
 * request ordering contract — lives in [BucketOverrideEditor], shared with the
 * Android and iOS sheets. This file is DOM plus one HTTP client.
 */
object BucketEditModal {

    private const val INITIAL_ROWS = 6

    // Kotlin has no nested type alias, so the shared types are re-exposed here
    // under the names this file and its test already use. No logic lives in
    // these — they are pure pass-throughs to [BucketOverrideEditor].

    /** @see BucketOverrideEditor.Sub */
    internal fun Sub(code: String, amount: Double) = BucketOverrideEditor.Sub(code, amount)

    /** @see BucketOverrideEditor.Row */
    internal fun Row(code: String, amount: Double, subs: List<BucketOverrideEditor.Sub>) =
        BucketOverrideEditor.Row(code, amount, subs)

    /** @see BucketOverrideEditor.Api */
    internal interface BucketApi : BucketOverrideEditor.Api

    /** @see BucketOverrideEditor.buildRows */
    internal fun buildRows(flow: BucketFlow): List<BucketOverrideEditor.Row> =
        BucketOverrideEditor.buildRows(flow)

    /**
     * Runs a shared write sequence and folds the result back into [local], the
     * map the modal redraws from. On failure the map still advances to whatever
     * landed, so the redraw shows the server's state and not the user's wish.
     *
     * @see BucketOverrideEditor.applySet
     */
    internal suspend fun applySet(
        row: BucketOverrideEditor.Row,
        code: String,
        bucket: BudgetBucket,
        local: MutableMap<String, BudgetBucket>,
        api: BucketOverrideEditor.Api = HttpBucketApi,
    ): Boolean = adopt(local, BucketOverrideEditor.applySet(row, code, bucket, local, api))

    /** @see BucketOverrideEditor.applyClear */
    internal suspend fun applyClear(
        row: BucketOverrideEditor.Row,
        code: String,
        local: MutableMap<String, BudgetBucket>,
        api: BucketOverrideEditor.Api = HttpBucketApi,
    ): Boolean = adopt(local, BucketOverrideEditor.applyClear(row, code, local, api))

    private fun adopt(
        local: MutableMap<String, BudgetBucket>,
        result: BucketOverrideEditor.EditResult,
    ): Boolean {
        local.clear()
        local.putAll(result.overrides)
        return result.ok
    }

    private fun toggleBtn(rowCode: String, code: String, b: BudgetBucket, on: Boolean): String {
        val t = WebTheme.current
        return """<button class="bucket-act" data-act="set" data-row="$rowCode" data-code="$code" data-bucket="${b.wire}" style="
            padding:4px 10px; border-radius:6px; font-size:12px; cursor:pointer;
            border:1px solid ${if (on) t.accent else t.border};
            background:${if (on) "${t.accent}1a" else "transparent"};
            color:${if (on) t.accent else t.textSecondary};
        ">${b.displayLabel()}</button>"""
    }

    private fun resetBtn(rowCode: String, code: String): String {
        val t = WebTheme.current
        val label = StringKey.BUDGET_BUCKET_RESET.tr()
        return """<button class="bucket-act" data-act="clear" data-row="$rowCode" data-code="$code"
            title="$label" aria-label="$label" style="
            padding:4px 8px; border-radius:6px; font-size:12px; cursor:pointer;
            border:1px solid ${t.border}; background:transparent; color:${t.textSecondary};
        ">↺</button>"""
    }

    /**
     * One detailed strand under an expanded primary.
     *
     * A strand whose code EQUALS the primary's — a transaction with no detailed
     * code, or a single-level `user_category` — has no key of its own to be
     * overridden against (BucketFlowBuilder keys it `detailedCode ?: code`, and
     * `effectiveBucket` only consults the detailed code when it differs from the
     * primary). Giving it toggles would be a trap: they would take the primary
     * path in [BucketOverrideEditor.applySet] and re-bucket the ENTIRE group from
     * a control that looks like it moves one strand. It is rendered read-only
     * instead, showing the bucket it inherits from the primary row above.
     */
    internal fun subRowHtml(
        r: BucketOverrideEditor.Row,
        s: BucketOverrideEditor.Sub,
        local: Map<String, BudgetBucket>,
    ): String {
        val t = WebTheme.current
        val b = BudgetClassificationEngine.effectiveBucket(r.code, s.code, local)
        val controls = if (s.code == r.code) {
            """<div title="${StringKey.BUDGET_BUCKET_FOLLOWS_PRIMARY.tr()}" style="
                font-size:12px; color:${t.textSecondary}; opacity:0.75;
            ">${b.displayLabel()}</div>"""
        } else {
            """
                ${toggleBtn(r.code, s.code, BudgetBucket.NEEDS, b == BudgetBucket.NEEDS)}
                ${toggleBtn(r.code, s.code, BudgetBucket.WANTS, b == BudgetBucket.WANTS)}
                ${if (local.containsKey(s.code)) resetBtn(r.code, s.code) else ""}
            """.trimIndent()
        }
        return """
            <div style="display:flex; align-items:center; justify-content:space-between; gap:8px; padding:6px 0 6px 20px;">
                <div style="min-width:0;">
                    <div style="color:${t.textSecondary}; font-size:12px;">${utils.CategoryUtils.formatTwoLevelCategoryLocalized(r.code, s.code)}</div>
                    <div style="color:${t.textSecondary}; font-size:11px; opacity:0.75;">${'$'}${s.amount.toInt()}</div>
                </div>
                <div style="display:flex; gap:6px; align-items:center;">
                    $controls
                </div>
            </div>
        """.trimIndent()
    }

    /**
     * @param overrides the caller's saved classifications. The modal keeps its own
     *   copy so it can redraw a row into the state a click just produced without
     *   waiting for [onSaved] to round-trip. Callers must not open the modal
     *   before that map has loaded — see WebDashboardRedesigned.openBucketEditModal.
     */
    fun show(flow: BucketFlow, overrides: Map<String, BudgetBucket>, onSaved: () -> Unit) {
        val t = WebTheme.current
        val rows = BucketOverrideEditor.buildRows(flow)

        val local = overrides.toMutableMap()
        val expanded = mutableSetOf<String>()
        var showAll = false
        var busy = false

        val overlay = document.createElement("div") as HTMLElement
        overlay.style.cssText = """
            position: fixed; inset: 0; z-index: 500;
            background: rgba(0,0,0,0.55);
            display: flex; align-items: center; justify-content: center;
        """.trimIndent()

        fun rowHtml(r: BucketOverrideEditor.Row): String {
            val current = BucketOverrideEditor.singleBucket(r, local) // null = mixed
            val overridden = BucketOverrideEditor.isOverridden(r, local)
            val open = r.code in expanded

            val mixedBadge = if (current != null) "" else
                """<span style="margin-left:6px; padding:1px 6px; border-radius:5px; font-size:10px;
                    border:1px solid ${t.border}; color:${t.textSecondary};
                ">${StringKey.BUDGET_BUCKET_MIXED.tr()}</span>"""

            val caret = if (r.subs.size < 2) "" else
                """<button class="bucket-act" data-act="expand" data-row="${r.code}"
                    title="${StringKey.BUDGET_BUCKET_MIXED_HINT.tr()}" style="
                    background:transparent; border:none; cursor:pointer; padding:4px 6px 4px 0;
                    font-size:12px; color:${t.textSecondary};
                ">${if (open) "▾" else "▸"}</button>"""

            return """
                <div style="padding:8px 0; border-bottom:1px solid ${t.border};">
                    <div style="display:flex; align-items:center; justify-content:space-between; gap:8px;">
                        <div style="display:flex; align-items:center; min-width:0;">
                            $caret
                            <div>
                                <div style="color:${t.textPrimary}; font-size:13px;">${utils.CategoryUtils.formatCategoryNameLocalized(r.code)}$mixedBadge</div>
                                <div style="color:${t.textSecondary}; font-size:11px;">${'$'}${r.amount.toInt()}</div>
                            </div>
                        </div>
                        <div style="display:flex; gap:6px; align-items:center;">
                            ${toggleBtn(r.code, r.code, BudgetBucket.NEEDS, current == BudgetBucket.NEEDS)}
                            ${toggleBtn(r.code, r.code, BudgetBucket.WANTS, current == BudgetBucket.WANTS)}
                            ${if (overridden) resetBtn(r.code, r.code) else ""}
                        </div>
                    </div>
                    ${if (open) r.subs.joinToString("") { subRowHtml(r, it, local) } else ""}
                </div>
            """.trimIndent()
        }

        overlay.innerHTML = """
            <div style="background:${t.card}; border:1px solid ${t.border}; border-radius:14px;
                        padding:20px; width:min(460px, 92vw); max-height:80vh; overflow:auto;">
                <h3 style="margin:0 0 12px; color:${t.textPrimary}; font-size:16px;">
                    ${StringKey.BUDGET_EDIT_BUCKETS_TITLE.tr()}
                </h3>
                <div id="bucket-rows"></div>
                <button id="bucket-show-all" style="
                    margin-top:10px; background:transparent; border:none; color:${t.accent};
                    font-size:12px; cursor:pointer;">${StringKey.BUDGET_EDIT_BUCKETS_SHOW_ALL.tr()}</button>
                <div id="bucket-status" aria-live="polite" style="
                    margin-top:10px; min-height:16px; font-size:12px; color:${t.textSecondary};"></div>
                <div style="text-align:right; margin-top:6px;">
                    <button id="bucket-close" style="
                        padding:8px 16px; border-radius:8px; border:1px solid ${t.border};
                        background:transparent; color:${t.textPrimary}; cursor:pointer;
                    ">${StringKey.ACTION_CLOSE.tr()}</button>
                </div>
            </div>
        """.trimIndent()

        document.body?.appendChild(overlay)

        val rowsEl = overlay.querySelector("#bucket-rows") as? HTMLElement
        val showAllEl = overlay.querySelector("#bucket-show-all") as? HTMLElement
        val statusEl = overlay.querySelector("#bucket-status") as? HTMLElement

        fun status(msg: String, color: String) {
            statusEl?.textContent = msg
            statusEl?.style?.color = color
        }

        /** Full redraw. Cheap at this size, and it keeps "Show all" from resurrecting
         *  the pre-edit state the way a cached row list used to. */
        fun render() {
            rowsEl?.innerHTML = (if (showAll) rows else rows.take(INITIAL_ROWS))
                .joinToString("") { rowHtml(it) }
            showAllEl?.style?.display = if (showAll || rows.size <= INITIAL_ROWS) "none" else "block"
        }
        render()

        // Delegated: rows are replaced wholesale on every redraw, so per-button
        // handlers would have to be re-attached each time.
        rowsEl?.addEventListener("click", { ev ->
            val el = ev.target as? HTMLElement
            if (el != null && el.classList.contains("bucket-act")) {
                val rowCode = el.getAttribute("data-row") ?: ""
                val row = rows.firstOrNull { it.code == rowCode }
                val act = el.getAttribute("data-act")
                if (row != null && act == "expand") {
                    if (!expanded.remove(rowCode)) expanded.add(rowCode)
                    render()
                } else if (row != null && !busy) {
                    busy = true
                    status("", t.textSecondary)
                    val code = el.getAttribute("data-code") ?: rowCode
                    val bucket = BudgetBucket.fromWire(el.getAttribute("data-bucket"))
                    GlobalScope.launch {
                        val ok = if (act == "set") applySet(row, code, bucket, local)
                        else applyClear(row, code, local)
                        busy = false
                        render()
                        if (ok) {
                            status(StringKey.SUCCESS_SAVED.tr(), t.incomeGreen)
                            onSaved()
                        } else {
                            status(StringKey.ERROR_GENERIC.tr(), t.expenseRed)
                        }
                    }
                }
            }
        })

        showAllEl?.onclick = {
            showAll = true
            render()
            Unit
        }

        (overlay.querySelector("#bucket-close") as? HTMLElement)?.onclick = {
            overlay.remove(); Unit
        }

        // Click on the backdrop (outside the card) dismisses too. Only fires when the
        // target IS the overlay itself — clicks inside the card bubble up but keep their
        // own target, so they don't close it.
        overlay.addEventListener("click", { ev ->
            if (ev.target === overlay) overlay.remove()
        })
    }

    /** Request body for PUT /api/budget/classifications. Serialized, not interpolated,
     *  so a category code carrying a quote can't produce malformed JSON. */
    @kotlinx.serialization.Serializable
    private data class BucketClassificationRequest(val categoryCode: String, val bucket: String)

    /** The real endpoints — the ones the old Budget-page chips already used. */
    private object HttpBucketApi : BucketApi {
        /** PUT /api/budget/classifications. */
        override suspend fun put(categoryCode: String, bucketWire: String): Boolean = send(
            method = "PUT",
            url = "${getBaseUrl()}/api/budget/classifications",
            body = kotlinx.serialization.json.Json.encodeToString(
                BucketClassificationRequest(categoryCode, bucketWire),
            ),
        )

        /** DELETE /api/budget/classifications/{categoryCode} — back to the engine default. */
        override suspend fun delete(categoryCode: String): Boolean = send(
            method = "DELETE",
            url = "${getBaseUrl()}/api/budget/classifications/${js("encodeURIComponent")(categoryCode) as String}",
            body = null,
        )
    }

    private suspend fun send(method: String, url: String, body: String?): Boolean = try {
        val token = ApiService.getSharedAuthToken()
        if (token == null) false else {
            val init = if (body == null) {
                RequestInit(
                    method = method,
                    headers = json("Authorization" to "Bearer $token"),
                )
            } else {
                RequestInit(
                    method = method,
                    headers = json(
                        "Authorization" to "Bearer $token",
                        "Content-Type" to "application/json",
                    ),
                    body = body,
                )
            }
            kotlinx.browser.window.fetch(url, init).await().ok
        }
    } catch (e: Throwable) {
        console.warn("Bucket classification $method failed: ${e.message}")
        false
    }

    /** Mirrors the pattern already used by WebDashboardRedesigned/WebBudgetPlanningComponent. */
    private fun getBaseUrl(): String =
        if (kotlinx.browser.window.location.hostname == "localhost" ||
            kotlinx.browser.window.location.hostname == "127.0.0.1"
        ) "http://localhost:3000" else ""
}
